import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'
import { Button } from '@/components/ui/button'
import { Label } from '@/components/ui/label'
import { FiscalYearPicker } from '@/components/sales-trackers/FiscalYearPicker'
import { useBillingsPeriod, periodQuery } from '@/hooks/use-billings-period'
import { RepSheet } from '@/components/sales-trackers/RepSheet'
import { TargetsDialog } from '@/components/sales-trackers/TargetsDialog'
import {
  useBillingsFiscalYears,
  useBillingsRepDetail,
  useBillingsRepRoster,
  useBillingsTargets,
} from '@/hooks/use-api'
import { useDocumentTitle } from '@/hooks/use-document-title'
import { explainError } from '@/lib/explain-error'
import { exportToCSV } from '@/utils/csvExport'

/**
 * By Rep: pick one rep, see their sheet.
 *
 * This replaces an all-reps league table that duplicated the one Overview already
 * shows. Jason's note: "rather than a dashboard of all reps, a drop-down list of
 * individual reps", because this tab is what a rep will eventually see of themselves,
 * and a page whose default view is everyone's numbers cannot become that.
 *
 *
 * ADDRESSED BY USER ID WHERE POSSIBLE
 *
 * The dropdown's value is a rep's user id when they are a CRM user, and a label only
 * for the two buckets that are not people. That matters beyond tidiness: a label is a
 * snapshot of somebody's current name, and two reps sharing a label would collide. The
 * `005` prefix is how the API tells the two apart, which is the convention already in
 * billings.js.
 *
 *
 * ONE IMPLEMENTATION, TWO ROUTES
 *
 * /sales-trackers/reps/:ident renders this same page with the dropdown preselected,
 * rather than a second page that would drift from this one. Overview's rep table links
 * there and the accessibility spec navigates there, so the route keeps working.
 *
 * Reps do NOT have access yet. Opening it to them is a new session-scoped endpoint
 * that ignores the parameter and reads req.user.id, never a widened guard on these
 * routes: every read here takes the rep from the URL.
 */
interface RepsProps {
  /** Set by the /reps/:ident route. A user id or a bucket label. */
  initialIdent?: string
}

export default function SalesTrackersReps({ initialIdent }: RepsProps) {
  useDocumentTitle('Billings by rep')

  /*
   * The fiscal year lives in the URL, not in state, and that is not a preference.
   *
   * /sales-trackers/reps and /sales-trackers/reps/:ident are separate routes with
   * separate elements, so choosing a rep unmounts this page and mounts a fresh copy.
   * Held in useState, the fiscal year silently snapped back to the latest year every
   * time somebody picked a rep - so you could look at FY2025, click a rep, and be
   * reading FY2027 without being told.
   *
   * In the query string it survives the remount, and it makes the URL genuinely
   * shareable, which is what the deep link was for.
   */
  const years = useBillingsFiscalYears()
  // The years are passed so this page defaults to the current month like every other one.
  // Without them the hook cannot tell whether the current month's year is on offer, and
  // quietly falls back to a whole year - so By Rep was the one page that ignored the
  // default everything else had.
  const period = useBillingsPeriod(years.data)
  const { fy, granularity, month, quarter, setPeriod, search } = period

  const [ident, setIdent] = useState<string | undefined>(initialIdent)
  const [editingTargets, setEditingTargets] = useState(false)
  const navigate = useNavigate()

  // availableFiscalYears is ordered newest first, and only offers years that hold
  // data, so [0] is the same year the server would have picked.
  const effectiveFy = fy ?? years.data?.[0]?.fiscal_year
  const roster = useBillingsRepRoster(effectiveFy)
  const repTargets = useBillingsTargets(effectiveFy, 'rep', editingTargets)
  const query = { fy: effectiveFy, ...periodQuery(period) }

  // Read alongside RepSheet's own query rather than threaded through it: both use the
  // same query key, so this is the cache, not a second request.
  const rep = useBillingsRepDetail(ident, query)
  const data = rep.data

  // A deep link changing under us (Overview links to a different rep) has to move the
  // dropdown, or the page would show one rep and select another.
  useEffect(() => {
    if (initialIdent) setIdent(initialIdent)
  }, [initialIdent])

  const choose = (value: string) => {
    setIdent(value || undefined)
    // Keep the address bar honest, so the page can be shared and the browser's back
    // button steps between reps. The WHOLE query string is carried, because this
    // navigation remounts the page and anything not in the URL is lost.
    //
    // Rebuilt from the live params rather than hand-assembled from `fy`: the old version
    // read `fy` and so carried nothing at all unless somebody had touched the picker,
    // which silently dropped the default period on every click through to a rep.
    const qs = search ? `?${search}` : ''
    if (value) navigate(`/sales-trackers/reps/${encodeURIComponent(value)}${qs}`)
    else navigate(`/sales-trackers/reps${qs}`)
  }

  const reps = roster.data ?? []
  const people = reps.filter((r) => r.can_hold_target)
  const buckets = reps.filter((r) => !r.can_hold_target)
  const selected = reps.find((r) => (r.rep_user_id ?? r.rep_label) === ident)

  return (
    <section className="p-6 space-y-6" aria-labelledby="sales-trackers-heading">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 id="sales-trackers-heading" className="text-2xl font-semibold">
            Billings by rep
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            Net billings from QuickBooks, including house accounts. Totals are CAD.
          </p>
        </div>
        {data && data.lines.length > 0 && (
          <Button
            variant="outline"
            onClick={() =>
              exportToCSV({
                filename: `billings-${data.rep_label ?? ident}-FY${data.fiscal_year}.csv`,
                // Exact values, not the rounded figures shown on screen.
                data: data.lines.map((l) => ({
                  Date: l.txn_date,
                  Customer: l.customer_name ?? '',
                  'Product/Service': l.product_service ?? '',
                  'Sales account': l.sales_account ?? '',
                  Invoice: l.doc_number ?? '',
                  Type: l.txn_type ?? '',
                  Category: l.category,
                  House: l.is_house ? 'Yes' : 'No',
                  Amount: l.amount,
                })),
              })
            }
            aria-label="Export these billing lines to CSV"
          >
            Export CSV
          </Button>
        )}
      </div>

      <div className="flex flex-wrap items-end gap-4">
        {years.data && years.data.length > 0 && (
          <FiscalYearPicker
            years={years.data}
            value={effectiveFy}
            onChange={(next) => setPeriod({ fy: next })}
            period={{ granularity, month, quarter, onChange: setPeriod }}
          />
        )}

        <div>
          <Label htmlFor="rep-select">Rep</Label>
          <select
            id="rep-select"
            className="mt-1 block h-9 min-w-[16rem] rounded-md border border-input bg-background px-3 text-sm"
            value={ident ?? ''}
            onChange={(e) => choose(e.target.value)}
          >
            <option value="">Choose a rep…</option>
            {/* People and buckets are separated because they are different kinds of
                thing: 'GrackleDocs' and 'Unassigned' hold real revenue but are not
                anybody, and cannot be given a target. */}
            <optgroup label="People">
              {people.map((r) => (
                <option key={r.rep_label} value={r.rep_user_id ?? r.rep_label}>
                  {r.display}
                  {!r.in_data ? ' (no billings this year)' : ''}
                </option>
              ))}
            </optgroup>
            {buckets.length > 0 && (
              <optgroup label="Not a person">
                {buckets.map((r) => (
                  <option key={r.rep_label} value={r.rep_label}>
                    {r.display}
                  </option>
                ))}
              </optgroup>
            )}
          </select>
          <p className="text-xs text-muted-foreground mt-1">
            {reps.length} in the list. Retired reps are managed on Attribution.
          </p>
        </div>
      </div>

      <p role="status" aria-live="polite" className="sr-only">
        {rep.isFetching
          ? 'Loading this rep’s billings'
          : data
            ? `${data.rep_label ?? 'Rep'} loaded`
            : ''}
      </p>

      {roster.isError && (
        <Alert variant="destructive">
          <AlertTitle>Could not load the rep list</AlertTitle>
          <AlertDescription>
            {explainError(roster.error, 'Something went wrong loading the rep list.')}
          </AlertDescription>
        </Alert>
      )}

      <RepSheet
        ident={ident}
        query={query}
        // A bucket cannot hold a target, so it is not offered one.
        onEditTargets={
          selected?.can_hold_target || data?.rep_user_id ? () => setEditingTargets(true) : undefined
        }
      />

      {data && (
        <TargetsDialog
          open={editingTargets}
          onOpenChange={setEditingTargets}
          months={data.months}
          existing={(repTargets.data?.targets ?? []).filter((t) =>
            data.rep_user_id ? t.rep_user_id === data.rep_user_id : t.rep_label === data.rep_label
          )}
          scope="rep"
          repUserId={data.rep_user_id}
          repLabel={data.rep_user_id ? null : data.rep_label}
          subjectName={data.rep_label ?? undefined}
        />
      )}
    </section>
  )
}
